import { siteBusiness } from "../config/siteBusiness";
import { EventType } from "../types/Types";

export type JsonLdObject = Record<string, unknown>;

const baseUrlFrom = (siteUrl: string): string => siteUrl.replace(/\/$/, "");

const toIsoString = (value: string | null | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
};

const stripHtml = (value: string | null | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }
  return value.replace(/<[^>]+>/g, "").trim();
};

const parsePrice = (value: string | null | undefined): number | undefined => {
  if (!value) {
    return undefined;
  }
  const match = value.toString().match(/(\d+(?:[.,]\d+)?)/);
  if (!match) {
    return undefined;
  }
  const normalized = match[1].replace(",", ".");
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? undefined : parsed;
};

export const buildLocalBusinessJsonLd = (siteUrl: string): JsonLdObject => {
  const baseUrl = baseUrlFrom(siteUrl);
  const organizationId = `${baseUrl}/#organization`;

  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "Restaurant", "Hotel"],
    "@id": organizationId,
    name: siteBusiness.name,
    legalName: siteBusiness.legalName,
    url: baseUrl,
    logo: `${baseUrl}${siteBusiness.logo}`,
    ...(siteBusiness.image ? { image: `${baseUrl}${siteBusiness.image}` } : {}),
    telephone: siteBusiness.telephone,
    address: siteBusiness.address,
    geo: siteBusiness.geo,
    hasMap: siteBusiness.hasMap,
    contactPoint: siteBusiness.contactPoints,
    openingHoursSpecification: siteBusiness.openingHoursSpecification,
    award: siteBusiness.award,
    sameAs: siteBusiness.sameAs,
    ...(siteBusiness.currenciesAccepted
      ? { currenciesAccepted: siteBusiness.currenciesAccepted }
      : {}),
    paymentAccepted: siteBusiness.paymentAccepted,
    servesCuisine: siteBusiness.servesCuisine,
    menu: siteBusiness.menu,
    acceptsReservations: siteBusiness.acceptsReservations,
    checkinTime: siteBusiness.checkinTime,
    checkoutTime: siteBusiness.checkoutTime,
    amenityFeature: siteBusiness.amenityFeature,
  };
};

export const buildEventJsonLd = (
  event: EventType["data"]["cms"]["entry"],
  siteUrl: string
): JsonLdObject => {
  const baseUrl = baseUrlFrom(siteUrl);
  const organizationId = `${baseUrl}/#organization`;

  const startDate = toIsoString(event.eventdateEventBegin);
  const doorTime = toIsoString(event.eventDateDoorsOpen);
  const image = event.overviewImage?.[0]?.url;

  const price =
    parsePrice(event.priceBookingInAdvance) ?? parsePrice(event.priceBoxOffice);

  const isFree = !event.ticketlink && price === undefined;

  const offers: JsonLdObject[] = [];
  if (event.ticketlink) {
    offers.push({
      "@type": "Offer",
      url: event.ticketlink,
      ...(price !== undefined ? { price, priceCurrency: "CHF" } : {}),
      availability: "https://schema.org/InStock",
    });
  } else if (isFree) {
    offers.push({
      "@type": "Offer",
      price: 0,
      priceCurrency: "CHF",
      availability: "https://schema.org/InStock",
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "Event",
    "@id": `${event.url}#event`,
    name: event.title,
    description: stripHtml(event.overviewDescription),
    url: event.url,
    inLanguage: event.language,
    ...(startDate ? { startDate } : {}),
    ...(doorTime ? { doorTime } : {}),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    isAccessibleForFree: isFree,
    location: { "@id": organizationId },
    organizer: { "@id": organizationId },
    ...(event.eventPerformer
      ? {
          performer: { "@type": "PerformingGroup", name: event.eventPerformer },
        }
      : {}),
    ...(image ? { image } : {}),
    ...(offers.length > 0 ? { offers } : {}),
  };
};
